import React, { useEffect, useState } from "react";

import {
  Box,
  Button,
  ErrorContent,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalManager,
  Text,
} from "src/components";
import { LivenessModalFooter } from "./Liveness.footer";
import ErrorImg from "./assets/errors.svg";
import {
  MediaDeviceType,
  useMediaDevice,
  useTranslation,
  useBreakpoint,
} from "src/hooks";
import styled, { css } from "styled-components";
import Camera from "./assets/ic_camera.svg";
import SmileyFace from "./assets/icon_smiley_face.svg";

enum ErrorType {
  NO_CAMERA = "no_camera",
  PERMISSION_DENIED = "permission_denied",
}

type Props = {
  show: boolean;
  onClose: () => any;
  onUseCamera: () => any;
  onUseQR: () => any;
};

export const InitLivenessModal = ({
  onClose,
  show,
  onUseCamera,
  onUseQR,
}: Props) => {
  const translate = useTranslation();
  const { isMobile } = useBreakpoint();
  const { hasDevice, requestPermission } = useMediaDevice(
    MediaDeviceType.VIDEO_INPUT
  );
  const [error, setError] = useState<ErrorType | null>(null);

  const getErrorTranslation = () => {
    switch (error) {
      case ErrorType.NO_CAMERA:
        return {
          title: translate(
            "owner_info.liveness.init_modal.errors.webcam.title"
          ),
          description: translate(
            "owner_info.liveness.init_modal.errors.webcam.description"
          ),
        };
      case ErrorType.PERMISSION_DENIED:
        return {
          title: translate(
            "owner_info.liveness.init_modal.errors.permission.title"
          ),
          description: translate(
            "owner_info.liveness.init_modal.errors.permission.description"
          ),
        };
      default:
        return { title: "", description: "" };
    }
  };

  const handleVerifyCameraPermission = async () => {
    if (hasDevice !== null && !hasDevice) {
      setError(ErrorType.NO_CAMERA);
      return;
    }
    const isAccepted = await requestPermission();
    if (!isAccepted) {
      setError(ErrorType.PERMISSION_DENIED);
      return;
    }

    return onUseCamera();
  };

  useEffect(() => {
    if (!show) {
      setError(null);
    }
  }, [show]);

  return (
    <ModalManager onClose={onClose} showModal={show}>
      <Modal wrapContent={true}>
        <ModalHeader />
        <ModalContent>
          <Box
            flex
            justifyCenter
            alignCenter
            flexDirectionColumn
            style={{ gap: "16px" }}
          >
            {error && (
              <Box paddingRightXl paddingLeftXl width100>
                <ErrorContent
                  image={ErrorImg}
                  title={getErrorTranslation().title}
                  description={getErrorTranslation().description}
                  outlined={true}
                />
              </Box>
            )}
            {!error && (
              <>
                <Text
                  fontSize="mlarge"
                  fontWeight="demi"
                  marginBottomL
                  marginTopS
                >
                  {translate("owner_info.liveness.init_modal.title")}
                </Text>
                <Requirements />
                <Button onClick={handleVerifyCameraPermission} width100>
                  {translate("owner_info.liveness.cta.continue")}
                </Button>
              </>
            )}
          </Box>
        </ModalContent>
        {!isMobile && (
          <ModalFooter>
            <LivenessModalFooter
              description={translate(
                "owner_info.liveness.init_modal.liveness_options.description"
              )}
              buttonText={translate(
                "owner_info.liveness.init_modal.liveness_options.cta"
              )}
              onButtonClick={onUseQR}
            />
          </ModalFooter>
        )}
      </Modal>
    </ModalManager>
  );
};

const RequirementsWrapperEl = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 12px;

  ${(p) => css`
    border: 1px solid ${p.theme.colors.borders.appBar};
    border-radius: 6px;
    background-color: ${p.theme.colors.background.light};
  `}
`;

const ItemEl = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding-bottom: 12px;
  padding-top: 12px;
  gap: 16px;
  ${(p) => css`
    border-bottom: 1px solid ${p.theme.colors.borders.appBar};
  `}
`;

type ItemProps = {
  icon: any;
  text: string;
};

const Item = ({ icon, text }: ItemProps) => {
  return (
    <ItemEl>
      <img src={icon} alt="requirement item" />
      <Text>{text}</Text>
    </ItemEl>
  );
};

const Requirements = () => {
  const translate = useTranslation();

  return (
    <RequirementsWrapperEl>
      <Item
        icon={Camera}
        text={translate("owner_info.liveness.init_modal.point_1")}
      />
      <Item
        icon={SmileyFace}
        text={translate("owner_info.liveness.init_modal.point_2")}
      />
    </RequirementsWrapperEl>
  );
};
